/**
 * PsrWaypointSelector picks a small, well-spread set of secure parking locations to inject into the
 * route as waypoints, so Google reroutes a "safety" variant that hugs secure parking. This is what
 * actively *changes* the route, beyond merely scoring Google's own alternatives.
 *
 * Algorithm:
 *   1. Take the PSRs already found inside the baseline corridor (level-filtered), with their along-route
 *      and perpendicular distances (from RouteScorer.psrInCorridor).
 *   2. Slice the route into buckets of width `psrSpacing`; assign each PSR to its bucket.
 *   3. In each bucket keep the single highest-utility PSR (prefer higher security level, closer to the
 *      natural route, away from incidents). Empty buckets are unavoidable coverage gaps.
 *   4. Cap the selection to MAX_WAYPOINTS (Google allows 23; we stay well under to leave headroom and
 *      avoid over-constraining the route), preserving spatial spread, and order by along-route distance.
 */

import { featureLatLng, minDistanceToPath } from "./GeoUtils";
import { levelWeight } from "./RouteScorer";
import { incidentWeight } from "./IncidentAvoidance";

export const MAX_WAYPOINTS = 8;

// Utility tuning: penalty per metre of perpendicular detour, and per unit of nearby incident weight.
const K_PERP = 1 / 4000; // ~ -1 point per 4 km off-route
const K_INCIDENT = 0.5;

/**
 * Nearby incident weight for a PSR (incidents within `dangerRadius` of the PSR point).
 */
function nearbyIncidentWeight(psrLatLng, incidents, config, nowSeconds) {
  if (!config.avoidIncidents || !incidents.length) {
    return 0;
  }
  let weight = 0;
  incidents.forEach((feature) => {
    const d = minDistanceToPath(featureLatLng(feature), [psrLatLng]).distance;
    if (d <= config.dangerRadius) {
      weight += incidentWeight(feature, nowSeconds);
    }
  });
  return weight;
}

/**
 * @param {Array<{feature: object, alongDist: number, perpDist: number, level: number}>} psrEntries
 *        from RouteScorer.psrInCorridor for the baseline route
 * @param {number} totalLength baseline route length (metres)
 * @param {google.maps.Data.Feature[]} incidentFeatures pre-filtered incidents (or [])
 * @param {object} config
 * @param {number} nowSeconds
 * @returns {{waypoints: google.maps.LatLng[], picked: Array, emptyBuckets: number}}
 */
export function selectWaypoints(psrEntries, totalLength, incidentFeatures, config, nowSeconds) {
  if (!psrEntries.length || totalLength <= 0) {
    return { waypoints: [], picked: [], emptyBuckets: 0 };
  }

  const spacing = Math.max(config.psrSpacing, 1000);
  const bucketCount = Math.max(1, Math.ceil(totalLength / spacing));

  /** @type {Map<number, {entry: object, util: number}>} */
  const best = new Map();
  let emptyBuckets = 0;

  psrEntries.forEach((entry) => {
    const bucket = Math.min(bucketCount - 1, Math.floor(entry.alongDist / spacing));
    const util =
      levelWeight(entry.level) -
      K_PERP * entry.perpDist -
      K_INCIDENT * nearbyIncidentWeight(featureLatLng(entry.feature), incidentFeatures, config, nowSeconds);
    const current = best.get(bucket);
    if (!current || util > current.util) {
      best.set(bucket, { entry, util });
    }
  });

  // Count buckets with no candidate at all (true coverage gaps the user can't fill).
  for (let b = 0; b < bucketCount; b++) {
    if (!best.has(b)) {
      emptyBuckets++;
    }
  }

  let picked = [...best.values()].map((b) => b.entry).sort((a, b) => a.alongDist - b.alongDist);

  // Cap while keeping spatial spread: if over the limit, keep evenly-indexed picks.
  if (picked.length > MAX_WAYPOINTS) {
    const step = picked.length / MAX_WAYPOINTS;
    const trimmed = [];
    for (let i = 0; i < MAX_WAYPOINTS; i++) {
      trimmed.push(picked[Math.floor(i * step)]);
    }
    picked = trimmed;
  }

  const waypoints = picked.map((entry) => featureLatLng(entry.feature));
  return { waypoints, picked, emptyBuckets };
}
